// frontend/src/pages/BulkImporterPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Paper, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails,
  Checkbox, Chip, Card, CardMedia, CardContent, CardActions,
  Grid, FormControlLabel, Alert
} from '@mui/material';
import {
  ExpandMore, Search as SearchIcon, ShoppingCart, CheckBox, CheckBoxOutlineBlank
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-toastify';

const BulkImporterPage = () => {
  const [researchText, setResearchText] = useState('');
  const [keywordsText, setKeywordsText] = useState('');
  const [searchMode, setSearchMode] = useState('ai'); // 'ai' or 'manual'
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [storeId, setStoreId] = useState(null);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await api.get('/api/stores');
        const data = res.data;
        if (data && data.length > 0) {
          setStoreId(data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch store:', err);
      }
    };
    fetchStore();
  }, []);

  const handleAnalyze = async () => {
    if (searchMode === 'ai' && !researchText.trim()) {
      toast.warn('Please paste some market research data first.');
      return;
    }
    if (searchMode === 'manual' && !keywordsText.trim()) {
      toast.warn('Please enter some keywords first.');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResults(null);
    setSelectedProducts(new Set());
    setImportResults(null);

    try {
      const payload = { maxProductsPerKeyword: 4 };
      if (searchMode === 'ai') {
        payload.researchText = researchText;
      } else {
        // Send raw string – backend will split on commas and/or newlines
        payload.keywordsRaw = keywordsText;
      }

      const res = await api.post('/api/bulk-research/analyze', payload, { timeout: 120000 });
      const data = res.data;
      if (data.success) {
        setAnalysisResults(data.data);
        toast.success(`Found ${data.data.totalProducts} products across ${data.data.totalKeywords} keywords`);
      } else {
        toast.error(data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to search products');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const toggleKeywordGroup = (products) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      const allSelected = products.every(p => next.has(p.id));
      products.forEach(p => {
        if (allSelected) {
          next.delete(p.id);
        } else {
          next.add(p.id);
        }
      });
      return next;
    });
  };

  const selectAll = () => {
    if (!analysisResults) return;
    const all = new Set();
    analysisResults.keywords.forEach(kw => {
      kw.products.forEach(p => all.add(p.id));
    });
    setSelectedProducts(all);
  };

  const deselectAll = () => {
    setSelectedProducts(new Set());
  };

  const getAllProducts = () => {
    if (!analysisResults) return [];
    const products = [];
    analysisResults.keywords.forEach(kw => {
      kw.products.forEach(p => products.push(p));
    });
    return products;
  };

  const getSelectedProductObjects = () => {
    return getAllProducts().filter(p => selectedProducts.has(p.id));
  };

  const handleImport = async () => {
    if (!storeId) {
      toast.error('No Shopify store connected. Please connect a store first.');
      return;
    }
    const selected = getSelectedProductObjects();
    if (selected.length === 0) {
      toast.warn('No products selected.');
      return;
    }

    setIsImporting(true);
    setImportResults(null);

    try {
      const importPayload = selected.map(p => ({
        id: p.id,
        title: p.title,
        sourceSite: p.sourceSite || 'taobao',
        link: p.sourceUrl
      }));

      const res = await api.post('/api/bulk-research/import-selected', {
        products: importPayload, storeId
      });
      const data = res.data;
      if (data.success) {
        setImportResults(data);
        toast.success(`Imported ${data.summary.success}/${data.summary.total} products`);
      } else {
        toast.error(data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Failed to import products');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Bulk Importer
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Paste market research data below. AI will extract product keywords, search Taobao/1688, and let you select products to import to Shopify.
      </Typography>

      {/* Section 1: Research Input */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant={searchMode === 'ai' ? 'contained' : 'outlined'}
            onClick={() => setSearchMode('ai')}
          >
            Market Research AI
          </Button>
          <Button
            variant={searchMode === 'manual' ? 'contained' : 'outlined'}
            onClick={() => setSearchMode('manual')}
          >
            Direct Keywords
          </Button>
        </Box>

        {searchMode === 'ai' ? (
          <>
            <Typography variant="h6" gutterBottom>Market Research Data</Typography>
            <TextField
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              placeholder="Paste your market research data here... (AI will find products)"
              value={researchText}
              onChange={(e) => setResearchText(e.target.value)}
              sx={{ mb: 2 }}
            />
          </>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>Direct Keywords</Typography>
            <TextField
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              placeholder="Enter keywords, separated by commas or line breaks (e.g., ceramic vase, wooden bowl)"
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              sx={{ mb: 2 }}
            />
          </>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={<SearchIcon />}
          onClick={handleAnalyze}
          disabled={isAnalyzing || (searchMode === 'ai' ? !researchText.trim() : !keywordsText.trim())}
        >
          {isAnalyzing ? 'Searching...' : (searchMode === 'ai' ? 'Analyze Research' : 'Search Products')}
        </Button>
        {isAnalyzing && <LinearProgress sx={{ mt: 2 }} />}
      </Paper>

      {/* Section 2: Results Grid */}
      {analysisResults && analysisResults.keywords.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {analysisResults.totalProducts} products across {analysisResults.totalKeywords} keywords
            </Typography>
            <Box>
              <Button size="small" onClick={selectAll} sx={{ mr: 1 }}>Select All</Button>
              <Button size="small" onClick={deselectAll}>Deselect All</Button>
            </Box>
          </Box>

          {analysisResults.keywords.map((kwGroup, idx) => {
            const allInGroupSelected = kwGroup.products.length > 0 && kwGroup.products.every(p => selectedProducts.has(p.id));
            const someInGroupSelected = kwGroup.products.some(p => selectedProducts.has(p.id));

            return (
              <Accordion key={idx} defaultExpanded={idx < 3}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Checkbox
                      checked={allInGroupSelected}
                      indeterminate={someInGroupSelected && !allInGroupSelected}
                      onClick={(e) => { e.stopPropagation(); toggleKeywordGroup(kwGroup.products); }}
                      size="small"
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {kwGroup.keyword}
                    </Typography>
                    <Chip label={`${kwGroup.products.length} products`} size="small" color="primary" variant="outlined" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {kwGroup.products.map((product) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.id}>
                        <Card
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            border: selectedProducts.has(product.id) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 }
                          }}
                          onClick={() => toggleProduct(product.id)}
                        >
                          <CardMedia
                            component="img"
                            height="180"
                            image={product.picUrl ? (product.picUrl.startsWith('//') ? 'https:' + product.picUrl : product.picUrl) : 'https://via.placeholder.com/180'}
                            alt={product.title}
                            sx={{ objectFit: 'cover' }}
                          />
                          <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 1,
                                fontSize: '0.8rem',
                                lineHeight: 1.3
                              }}
                            >
                              <a
                                href={product.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                              >
                                {product.englishTitle || product.title}
                              </a>
                            </Typography>
                            <Typography variant="h6" color="error" sx={{ fontSize: '1rem', mb: 0.5 }}>
                              ¥{product.price}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                              {/* Sales */}
                              <Typography variant="caption" color="text.secondary">
                                🛒 {product.sales > 0 ? `${product.sales.toLocaleString()} sold/mo` : 'Sales: N/A'}
                              </Typography>
                              {/* Total stock */}
                              <Typography
                                variant="caption"
                                sx={{
                                  color: (product.stock !== 'N/A' && product.stock > 0) ? 'success.main' : 'error.main',
                                  fontWeight: 'bold'
                                }}
                              >
                                Stock: {product.stock !== undefined && product.stock !== 'N/A' ? Number(product.stock).toLocaleString() : 'N/A'}
                              </Typography>
                              {/* Variants */}
                              {product.totalVariants > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  📦 {product.totalVariants} variant{product.totalVariants !== 1 ? 's' : ''}
                                </Typography>
                              )}
                              {/* Out of stock variants in red */}
                              {product.outOfStockVariants > 0 && (
                                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                  ⚠️ {product.outOfStockVariants} out of stock
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                          <CardActions sx={{ p: 1, pt: 0 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedProducts.has(product.id)}
                                  icon={<CheckBoxOutlineBlank />}
                                  checkedIcon={<CheckBox />}
                                  size="small"
                                />
                              }
                              label="Select"
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleProduct(product.id)}
                              sx={{ m: 0 }}
                            />
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      )}

      {/* Section 3: Import Action Bar */}
      {selectedProducts.size > 0 && (
        <Paper
          sx={{
            p: 2,
            position: 'sticky',
            bottom: 16,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#f5f5f5',
            border: '1px solid #1976d2'
          }}
        >
          <Typography variant="subtitle1">
            {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<ShoppingCart />}
            onClick={handleImport}
            disabled={isImporting}
          >
            {isImporting ? 'Importing...' : `Import ${selectedProducts.size} to Shopify`}
          </Button>
        </Paper>
      )}

      {isImporting && <LinearProgress sx={{ mt: 2 }} />}

      {/* Import Results */}
      {importResults && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>Import Results</Typography>
          <Alert severity={importResults.summary.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {importResults.summary.success} of {importResults.summary.total} products imported successfully
            {importResults.summary.failed > 0 && ` (${importResults.summary.failed} failed)`}
          </Alert>
          {importResults.results && importResults.results.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Chip
                label={r.success ? 'OK' : 'FAIL'}
                color={r.success ? 'success' : 'error'}
                size="small"
              />
              <Typography variant="body2">
                {r.title || r.productId || `Product ${i + 1}`}
                {r.error && ` — ${r.error}`}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default BulkImporterPage;
